import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2, ImageOff, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MediaManager() {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const resolve = (src) => (src && src.startsWith("/") ? `${backend}${src}` : src);
  const qc = useQueryClient();

  const { data: media, isLoading } = useQuery({
    queryKey: ["admin-media"],
    queryFn: () => api.get("/admin/media").then((r) => r.data),
  });

  const remove = async (m) => {
    if (!window.confirm("Изтриване на изображението? Действието е окончателно.")) return;
    try {
      await api.delete(`/admin/media/${m.id}`);
      toast.success("Изображението е изтрито.");
      qc.invalidateQueries({ queryKey: ["admin-media"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="mt-6" data-testid="media-manager">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-bold text-slate-900">Качени изображения</h2>
        <span className="text-sm text-slate-500">
          {media ? `${media.length} файла` : ""}
        </span>
      </div>
      <p className="mb-5 text-sm text-slate-500">
        Тук са всички качени снимки. Използваните в статия или обява са защитени; неизползваните можете да изтриете, за да освободите място.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : (media || []).length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center text-slate-500">
          <ImageOff className="h-8 w-8 text-slate-300" />
          Все още няма качени изображения. Качете снимка при създаване на статия.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {media.map((m) => (
            <div
              key={m.id}
              className="group overflow-hidden rounded-xl border border-slate-200 bg-white"
              data-testid={`media-item-${m.id}`}
            >
              <div className="relative aspect-video bg-slate-100">
                <img src={resolve(m.url)} alt="" loading="lazy" className="h-full w-full object-cover" />
                <span
                  className={`absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    m.in_use ? "bg-green-600/90 text-white" : "bg-slate-900/70 text-white"
                  }`}
                  data-testid={`media-usage-${m.id}`}
                >
                  {m.in_use ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                  {m.in_use ? "Използва се" : "Свободно"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 p-3">
                <div className="min-w-0">
                  <div className="truncate text-xs font-semibold text-slate-700" title={m.original_filename}>
                    {m.original_filename || "изображение"}
                  </div>
                  <div className="text-[11px] text-slate-400">{formatSize(m.size)}</div>
                </div>
                <button
                  onClick={() => remove(m)}
                  disabled={m.in_use}
                  data-testid={`media-delete-${m.id}`}
                  title={m.in_use ? "Използва се в статия — не може да се изтрие" : "Изтрий"}
                  className="shrink-0 rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
